import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Typography,
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

const CustomPagination = ({
  page, rowsPerPage, count, onPageChange
}) => {
  const theme = useTheme();
  const totalPages = Math.ceil(count / rowsPerPage);

  // Calculate range for "Showing 1-10 of 50"
  const startRange = count === 0 ? 0 : page * rowsPerPage + 1;
  const endRange = Math.min((page + 1) * rowsPerPage, count);

  const handlePrev = () => {
    if (page > 0) onPageChange(null, page - 1);
  };

  const handleNext = () => {
    if (page < totalPages - 1) onPageChange(null, page + 1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 3;

    // Helper to create page button
    const createPage = (i) => (
      <Box
        key={i}
        onClick={() => onPageChange(null, i)}
        sx={{
          minWidth: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 0.5,
          borderRadius: 1.5,
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: page === i ? 600 : 500,
          backgroundColor: page === i ? theme.palette.primary.main : 'transparent',
          color: page === i ? '#fff' : theme.palette.text.primary,
          transition: 'all 0.2s',
          border: '1px solid',
          borderColor: page === i ? theme.palette.primary.main : 'transparent',
          '&:hover': {
            backgroundColor: page === i
              ? theme.palette.primary.dark
              : alpha(theme.palette.primary.main, 0.08),
            borderColor: page === i ? theme.palette.primary.dark : alpha(theme.palette.divider, 0.5)
          }
        }}
      >
        {i + 1}
      </Box>
    );

    // Always show first page
    pages.push(createPage(0));

    // Start Ellipsis
    if (page > maxVisible) {
      pages.push(
        <Typography key="start-ellipsis" color="text.secondary" sx={{ mx: 1 }}>
          ...
        </Typography>
      );
    }

    // Pages around current
    // Ensure we don't duplicate 0 or last page
    const start = Math.max(1, page - 1);
    const end = Math.min(totalPages - 2, page + 1);

    for (let i = start; i <= end; i++) {
      pages.push(createPage(i));
    }

    // End Ellipsis
    if (page < totalPages - maxVisible - 1) {
      pages.push(
        <Typography key="end-ellipsis" color="text.secondary" sx={{ mx: 1 }}>
          ...
        </Typography>
      );
    }

    // Always show last page if strictly greater than 1 page
    if (totalPages > 1) {
      pages.push(createPage(totalPages - 1));
    }

    return pages;
  };

  if (count === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2,
        px: 3,
        gap: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: '#FFFFFF'
      }}
    >
      {/* Left: Summary Text */}
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        Showing
        {' '}
        <Box component="span" fontWeight={700} color="text.primary">
          {startRange}
        </Box>
        -
        <Box component="span" fontWeight={700} color="text.primary">
          {endRange}
        </Box>
        {' '}
        of
        {' '}
        <Box component="span" fontWeight={700} color="text.primary">
          {count}
        </Box>
        {' '}
        entries
      </Typography>

      {/* Right: Controls */}
      <Stack direction="row" alignItems="center">
        <IconButton
          onClick={handlePrev}
          disabled={page === 0}
          size="small"
          sx={{
            mr: 1,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), borderColor: theme.palette.primary.main }
          }}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>

        {renderPageNumbers()}

        <IconButton
          onClick={handleNext}
          disabled={page === totalPages - 1}
          size="small"
          sx={{
            ml: 1,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), borderColor: theme.palette.primary.main }
          }}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};

CustomPagination.propTypes = {
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired
};

export default CustomPagination;
